/**
 * Phase 2e §1 — Employer portal census route.
 * Phase 3 shell consolidation — now renders inside <PortalChrome> so the
 * authenticated portal shell is consistent with the rest of /portal/*.
 *
 * Mounts the same <CensusUploadPanel> the admin console uses, but under
 * an employer session (`laguna_team_session`) and without any admin chrome.
 * The backend already dual-auths via `require_census_actor`, so we just
 * need to prove the session and surface the employer's active application.
 *
 * Path: /portal/:employerId/census
 * Guards:
 *   - No session → redirect to /team/login (→ /employer-signin)
 *   - Session's employer_id != :employerId → 403 screen (not a redirect; we
 *     want the user to see they're logged into a different org)
 *   - No active employer_application for this employer → soft 404 screen
 */
import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import CensusUploadPanel from "../components/CensusUploadPanel";
import PortalChrome from "../components/portal/PortalChrome";
import { employerCurrentApplication, employerMe } from "../lib/api";

import "./EmployerCensus.css";

export default function EmployerCensus() {
    const { t } = useTranslation();
    const { employerId } = useParams();
    const navigate = useNavigate();

    const [phase, setPhase] = useState("loading"); // loading | ok | not_logged_in | wrong_org | no_app | error
    const [session, setSession] = useState(null);
    const [app, setApp] = useState(null);
    const [errorDetail, setErrorDetail] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const me = await employerMe();
                if (!me?.email) {
                    setPhase("not_logged_in");
                    return;
                }
                setSession(me);
                if (me.employer_id && me.employer_id !== employerId) {
                    setPhase("wrong_org");
                    return;
                }
                try {
                    const appRes = await employerCurrentApplication();
                    if (!appRes?.application_id) {
                        setPhase("no_app");
                        return;
                    }
                    setApp(appRes);
                    setPhase("ok");
                } catch (e) {
                    const status = e?.response?.status;
                    if (status === 404) {
                        setPhase("no_app");
                    } else {
                        setErrorDetail(e?.response?.data?.detail || String(e));
                        setPhase("error");
                    }
                }
            } catch (e) {
                const status = e?.response?.status;
                if (status === 401) {
                    setPhase("not_logged_in");
                } else {
                    setErrorDetail(e?.response?.data?.detail || String(e));
                    setPhase("error");
                }
            }
        })();
    }, [employerId]);

    // 401 is the only case where we abandon the chrome — redirect to the
    // unified employer signin entry. Every other state renders inside
    // <PortalChrome> so the user always sees the authenticated shell.
    if (phase === "not_logged_in") {
        return <Navigate to="/team/login" replace />;
    }

    const displayName = session?.employer_name || session?.employer_id;

    return (
        <PortalChrome actorType="employer" displayName={displayName}>
            {phase === "loading" && (
                <div className="portal-census-wrap" data-testid="portal-census-loading">
                    <div className="portal-census-center">
                        <div className="cu-spinner" />
                        <div className="mono portal-census-loading-label">
                            {t("portalCensus.loading")}
                        </div>
                    </div>
                </div>
            )}

            {phase === "wrong_org" && (
                <div className="portal-census-wrap" data-testid="portal-census-wrong-org">
                    <div className="portal-census-center portal-census-card">
                        <div className="portal-census-label mono">403 · Forbidden</div>
                        <h1>{t("portalCensus.wrongOrgTitle")}</h1>
                        <p>{t("portalCensus.wrongOrgBody", { employer: session?.employer_name || session?.employer_id })}</p>
                        <div className="portal-census-actions">
                            <button
                                type="button"
                                className="admin-btn primary"
                                onClick={() => navigate("/")}
                                data-testid="portal-census-home"
                            >
                                {t("portalCensus.backHome")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {phase === "no_app" && (
                <div className="portal-census-wrap" data-testid="portal-census-no-app">
                    <div className="portal-census-center portal-census-card">
                        <div className="portal-census-label mono">{t("portalCensus.noAppLabel")}</div>
                        <h1>{t("portalCensus.noAppTitle")}</h1>
                        <p>{t("portalCensus.noAppBody")}</p>
                    </div>
                </div>
            )}

            {phase === "error" && (
                <div className="portal-census-wrap" data-testid="portal-census-error">
                    <div className="portal-census-center portal-census-card">
                        <div className="portal-census-label mono">Error</div>
                        <h1>{t("portalCensus.errorTitle")}</h1>
                        <p>{errorDetail}</p>
                    </div>
                </div>
            )}

            {phase === "ok" && (
                <div className="portal-census-wrap" data-testid="portal-census">
                    <header className="portal-census-header">
                        <div className="portal-census-crumb mono">
                            {session?.employer_name || session?.employer_id}
                        </div>
                        <h1 className="portal-census-title">
                            {t("portalCensus.pageTitle")}
                        </h1>
                        <p className="portal-census-sub">
                            {t("portalCensus.pageSub")}
                        </p>
                    </header>
                    <main className="portal-census-main">
                        <CensusUploadPanel
                            applicationId={app.application_id}
                            status={app.status}
                            employerId={session.employer_id}
                            mountPoint="employer_portal"
                            onDone={() => { /* refresh handled inside the panel */ }}
                        />
                    </main>
                </div>
            )}
        </PortalChrome>
    );
}
